import React from 'react';
import { BookOpen, Loader2 } from 'lucide-react';
import BuildDetailsPopover from '../admin/BuildDetailsPopover';
import { useStorybookStatus } from '../../context/StorybookStatusContext';
import { useHeaderTheme } from './HeaderThemeContext';

/**
 * Compact super-admin status pill rendered in the global app header.
 * Subscribes to the shared StorybookStatusContext — no per-component
 * polling — so multiple mount points (header, admin page) share a
 * single in-flight `/admin/storybook/status` request.
 */
const DOT_COLORS = {
  healthy: 'bg-emerald-500',
  rebuilding: 'bg-amber-500 animate-pulse',
  failed: 'bg-rose-500',
  stale: 'bg-slate-400',
  idle: 'bg-slate-400',
};

const LABELS = {
  healthy: 'Storybook',
  rebuilding: 'Rebuilding…',
  failed: 'Build failed',
  stale: 'Stale',
  idle: 'Storybook',
};

function classifyStatus(s) {
  if (!s) return 'idle';
  if (s.rebuilding) return 'rebuilding';
  if (s.last_exit_code !== null && s.last_exit_code !== 0) return 'failed';
  if (s.stale) return 'stale';
  if (s.last_exit_code === 0) return 'healthy';
  return 'idle';
}

export default function GlobalStorybookPill() {
  const { status } = useStorybookStatus();
  const { onDark } = useHeaderTheme();
  // Storybook is a dev/internal tool. In production the deploy
  // image doesn't ship yarn/node, so the backend reports
  // `runtime_capable: false`. When that's the case we hide the
  // pill entirely — there's nothing meaningful for the user to do
  // here, and a "Build failed" badge is misleading (the build was
  // never going to be possible in this environment).
  if (status && status.runtime_capable === false) {
    return null;
  }
  const kind = classifyStatus(status);

  const trigger = (
    <button
      type="button"
      title="Component Library status (super-admin)"
      data-testid="global-storybook-pill"
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors ${onDark ? 'bg-white/5 border-[#1f2a44] hover:bg-white/10 text-slate-200' : 'bg-slate-100 border-slate-200 hover:bg-slate-200 text-slate-700'}`}
    >
      {kind === 'rebuilding' ? (
        <Loader2 size={12} className={`animate-spin ${onDark ? 'text-amber-400' : 'text-amber-500'}`} />
      ) : (
        <BookOpen size={12} className={onDark ? 'text-slate-400' : 'text-slate-500'} />
      )}
      <span className={`h-1.5 w-1.5 rounded-full ${DOT_COLORS[kind]}`} />
      <span className="text-xs font-medium">{LABELS[kind]}</span>
    </button>
  );

  return <BuildDetailsPopover trigger={trigger} />;
}
